/**
 * Order Utility Functions
 * Pure functions for order data manipulation
 */

import type { OrderSummary, PromotionSummary, PaymentSummary, Subscription, OrderAddress } from '../types';

export interface OrderLineItem {
  id: string;
  orderId: string;
  productId: string;
  variantId: string;
  priceId: string;
  sku: string;
  unitAmount: number;
  quantity: number;
  amount: number;
  adjustedAmount: number;
  currency: string;
  adjustments?: Record<string, unknown>[];
  recurring: boolean;
  rebillMode?: string | null;
  rebillStepIntervalMs?: number | null;
  interval: string;
  intervalCount: number;
  totalBillingCycles: number;
  unitAmountAfterFirstCycle: number;
  subscriptionSettings?: Record<string, unknown> | null;
  properties?: Record<string, unknown>;
  orderLineItemProduct: {
    id: string;
    name: string;
    description: string;
    storefrontProductId: string;
    originalProductId: string;
    orderLineItemId: string;
  };
  orderLineItemVariant: {
    id: string;
    name: string;
    price: number | null;
    currency: string | null;
    imageUrl: string;
    description: string | null;
    storefrontVariantId: string | null;
    originalVariantId: string;
    orderLineItemId: string;
  };
}

export interface Order {
  id: string;
  orderNumber: number;
  storeId: string;
  accountId: string;
  createdAt: string;
  updatedAt: string;
  status: string;
  cancelledAt?: string | null;
  cancelledReason?: string | null;
  paidAt?: string | null;
  paidAmount: number;
  openAt: string;
  abandonedAt?: string | null;
  currency: string;
  externalCustomerType?: string | null;
  externalCustomerId?: string | null;
  externalOrderId?: string | null;
  billingAddress?: {
    company: string;
    firstName: string;
    lastName: string;
    address1: string;
    address2?: string;
    city: string;
    country: string;
    state: string;
    postal: string;
    phone: string;
    email?: string;
  };
  shippingAddress?: {
    company: string;
    firstName: string;
    lastName: string;
    address1: string;
    address2: string;
    city: string;
    country: string;
    state: string;
    postal: string;
    phone: string;
  };
  pickupAddress?: Record<string, unknown> | null;
  taxesIncluded: boolean;
  draft: boolean;
  checkoutSessionId: string;
  sessionHash: string;
  customerId: string;
  createdFrom: string;
  paymentInstrumentId: string;
  refundedAt?: string | null;
  refundedAmount?: number | null;
  metadata?: Record<string, unknown>;
  customer?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    externalCustomerId?: string | null;
    lastOrderId: string;
    accountId: string;
    storeId: string;
    billingAddress?: OrderAddress | null;
    shippingAddress?: OrderAddress | null;
    currency: string;
    locale: string;
    draft: boolean;
    acceptsMarketing: boolean;
    createdAt: string;
    updatedAt: string;
    metadata?: Record<string, unknown>;
    device?: Record<string, unknown> | null;
  };
  items: OrderLineItem[];
  summaries?: OrderSummary[];
  promotions?: PromotionSummary[];
  checkoutSession?: {
    id: string;
    selectedPresentmentCurrency?: string;
    returnUrl?: string;
    [key: string]: unknown;
  };
  payments?: PaymentSummary[];
  subscriptions?: Subscription[];
  store?: {
    name: string;
    chargeCurrencies: string[];
    presentmentCurrencies: string[];
  };
  relatedOrders?: Order[];
}

export class OrderUtils {
  /**
   * Get order line items
   */
  static getOrderLineItems(order: Order): OrderLineItem[] {
    return order.items || [];
  }

  /**
   * Get order total
   */
  static getOrderTotal(order: Order): number {
    return order.paidAmount || 0;
  }

  /**
   * Get order status
   */
  static getOrderStatus(order: Order): string {
    return order.status || 'unknown';
  }

  /**
   * Check if order is completed
   */
  static isOrderCompleted(order: Order): boolean {
    return order.status === 'completed' || order.status === 'fulfilled';
  }

  /**
   * Check if order is pending
   */
  static isOrderPending(order: Order): boolean {
    return order.status === 'pending' || order.status === 'processing';
  }

  /**
   * Check if order is cancelled
   */
  static isOrderCancelled(order: Order): boolean {
    return order.status === 'cancelled' || order.status === 'refunded';
  }
}
